import React, { useEffect, useState, useRef, lazy, Suspense } from "react";
import { FaMicrophone, FaStop } from "react-icons/fa";
import AudioIcon from "../../../assets/icons/audio";
import VideoIcon from "../../../assets/icons/video";
import SendIcon from "../../../assets/icons/send.svg";
import { ReactComponent as MicIcon } from "../../../assets/icons/mic.svg";
import logoIcon from "../../../assets/icons/logo.svg";
import aipsychologistIcon from "../../../assets/icons/aipsychologist.svg";
import aidieticianIcon from "../../../assets/icons/aidietician.svg";
import insightsIcon from "../../../assets/icons/insights.svg";
import SlideOver from "../components/SlideOver";
import {
  FiCopy,
  FiThumbsUp,
  FiThumbsDown,
  FiVolume2,
  FiEdit,
  FiSmile,
  FiRefreshCw,
  FiChevronDown, // <-- needed for the floating arrow
} from "react-icons/fi";

const InsightsPanel = lazy(() => import("../components/InsightsPanel"));
const JournalPanel = lazy(() => import("../components/JournalPanel"));
const MoodPanel = lazy(() => import("../components/Moodpanel"));

const FullScreenChat = ({
  handleModalOpen,
  isLoading,
  setIsLoading,
  addMessage,
  messagesEndRef,
  userInput,
  setUserInput,
  autoResize, // kept for compatibility
  sendMessage,
  messages,
  toggleRecording,
  showTyping,
  isRecording,
  chatHistory,
  selectedExpert, // coming from parent
}) => {
  const textareaRef = useRef(null);
  const [isOverflowFS, setIsOverflowFS] = useState(false);
  const baseHeightRef = useRef(null);

  // NEW: scroll-to-bottom arrow state/refs
  const messagesContainerRef = useRef(null);
  const [showScrollArrow, setShowScrollArrow] = useState(false);

  // slide-over
  const [panel, setPanel] = useState(null);
  const openPanel = (key) => setPanel(key);
  const closePanel = () => setPanel(null);

  // expert switch confirm
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingExpert, setPendingExpert] = useState(null);
  const [localExpert, setLocalExpert] = useState(null);

  const displayExpert = (localExpert ?? selectedExpert) || "Expert";

  const [chatFeedbacks, setChatFeedbacks] = useState({});
  const [retryingMsgs, setRetryingMsgs] = useState({});

  const handleSelectExpert = (expert) => {
    if ((localExpert ?? selectedExpert) === expert) return;
    setPendingExpert(expert);
    setShowConfirmation(true);
  };

  // message formatting stays as-is
  const formatMessage = (text) => {
    if (!text) return null;
    return text.split(/<br\s*\/?>/i).map((line, i, arr) => (
      <React.Fragment key={i}>
        {line
          .split(/(<strong>.*?<\/strong>)/gi)
          .filter(Boolean)
          .map((part, j) => {
            if (/^<strong>.*<\/strong>$/i.test(part)) {
              return <strong key={j}>{part.replace(/<\/?strong>/gi, "")}</strong>;
            }
            return <React.Fragment key={j}>{part}</React.Fragment>;
          })}
        {i < arr.length - 1 && <br />}
      </React.Fragment>
    ));
  };

  const cleanText = (htmlText) => {
    if (!htmlText) return "";
    return htmlText.replace(/<br\s*\/?>/gi, "\n").replace(/<\/?strong>/gi, "");
  };

  const readAloud = (text) => {
    if (!text) return;
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = "en-US";
    utterance.rate = 1;
    utterance.pitch = 1;
    window.speechSynthesis.speak(utterance);
  };

  function LocalData(feedback, msgid) {
    const rows = JSON.parse(localStorage.getItem("chat-feedback") || "[]");
    const existingIndex = rows.findIndex((row) => row.msgid === msgid);
    if (existingIndex !== -1) rows[existingIndex].feedback = feedback;
    else rows.push({ msgid, feedback });
    setChatFeedbacks((prev) => ({ ...prev, [msgid]: feedback }));
    localStorage.setItem("chat-feedback", JSON.stringify(rows));
  }

  function retrymsg(_, msgId) {
    setRetryingMsgs((prev) => ({ ...prev, [msgId]: true }));
    setTimeout(() => {
      setRetryingMsgs((prev) => {
        const updated = { ...prev };
        delete updated[msgId];
        return updated;
      });
    }, 2000);
  }

  const handleSend = () => {
    const trimmed = userInput.trim();
    if (!trimmed) return;
    addMessage(trimmed, "user");
    setUserInput("");
    setIsLoading(true);
    sendMessage(trimmed);
    const el = textareaRef.current;
    if (el) {
      el.style.height = "auto";
      setIsOverflowFS(false);
    }
  };

  const autoResizeInternal = () => {
    const el = textareaRef.current;
    if (!el) return;
    if (!el.value || !el.value.trim()) {
      el.style.height = `${baseHeightRef.current || 0}px`;
      setIsOverflowFS(false);
      return;
    }
    el.style.height = "auto";
    const cs = getComputedStyle(el);
    const maxH = parseFloat(cs.maxHeight);
    const maxPx = Number.isFinite(maxH) ? maxH : el.scrollHeight;
    el.style.height = `${Math.min(el.scrollHeight, maxPx)}px`;
    setIsOverflowFS(el.scrollHeight > el.clientHeight + 1);
  };

  // scroll helpers for the arrow
  const isNearBottom = (el) =>
    el.scrollHeight - el.scrollTop - el.clientHeight < 80;

  const handleScroll = () => {
    const el = messagesContainerRef.current;
    if (!el) return;
    setShowScrollArrow(!isNearBottom(el));
  };

  // Only auto-scroll if user is already near the bottom
  useEffect(() => {
    const el = messagesContainerRef.current;
    if (!el) return;
    if (isNearBottom(el)) {
      messagesEndRef?.current?.scrollIntoView({ behavior: "smooth" });
      setShowScrollArrow(false);
    } else {
      setShowScrollArrow(true);
    }
  }, [messagesEndRef, messages?.length]); // trigger when new message count changes

  // keep textarea scrolled to bottom as it grows (unchanged)
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "";
    const cs = getComputedStyle(el);
    const minH =
      parseFloat(cs.minHeight) ||
      (parseFloat(cs.lineHeight) || 20) +
        (parseFloat(cs.paddingTop) || 0) +
        (parseFloat(cs.paddingBottom) || 0);
    baseHeightRef.current = minH;
    el.style.height = `${minH}px`;
    setIsOverflowFS(false);
  }, []);

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    const handleAutoScroll = () => {
      el.scrollTop = el.scrollHeight;
    };
    el.addEventListener("input", handleAutoScroll);
    return () => el.removeEventListener("input", handleAutoScroll);
  }, []);

  // normalize messages (unchanged)
  const historyArray = Array.isArray(chatHistory)
    ? chatHistory
    : chatHistory?.messages || [];

  const historyMsgs = historyArray.map((m, idx) => ({
    id: m.id ?? m.index ?? idx,
    sender: m.sender ?? m.sender_id ?? "ai",
    text: m.text ?? m.message_text ?? "",
    timestamp: m.timestamp ?? m.createdat ?? null,
  }));

  const localMsgs = Array.isArray(messages) ? messages : [];
  const combinedMessages = [...historyMsgs, ...localMsgs];

  const isExpertMessage = (msg) => {
    const raw = msg?.sender;
    const s = String(raw ?? "").toLowerCase();
    const expertIds = [
      process.env.REACT_APP_DIETITIAN,
      process.env.REACT_APP_PSYCHOLOGIST,
    ]
      .filter(Boolean)
      .map(String);
    return (
      s === "ai" ||
      s === "assistant" ||
      s === "expert" ||
      s === "doctor" ||
      s === "dietician" ||
      expertIds.includes(String(raw))
    );
  };

  const scrollToBottom = () => {
    messagesEndRef?.current?.scrollIntoView({ behavior: "smooth" });
    const el = messagesContainerRef.current;
    if (el) setShowScrollArrow(!isNearBottom(el));
  };

  return (
    <div className="chat-fullscreen">
      {/* Messages */}
      <div
        className="chat-messages"
        ref={messagesContainerRef}
        onScroll={handleScroll}
      >
        {combinedMessages.map((msg) => {
          const isExpert = isExpertMessage(msg);
          return (
            <React.Fragment key={msg.id}>
              {isExpert && (
                <h3 className="ai-response-title">
                  <img src={logoIcon} alt="AI Logo" className="ai-logo" />
                  {displayExpert
                    ? displayExpert.charAt(0).toUpperCase() + displayExpert.slice(1)
                    : ""}
                </h3>
              )}

              <div className={`chat-bubble ${isExpert ? "expert-message" : "user-message"}`}>
                <div>
                  {retryingMsgs[msg.id] ? (
                    <i style={{ color: "gray" }}>Retrying to load...</i>
                  ) : (
                    String(msg.text || "")
                      .split("\n")
                      .map((line, i) => <div key={i}>{formatMessage(line)}</div>)
                  )}
                </div>

                {msg.timestamp && (
                  <div className="timestamp">
                    {new Date(msg.timestamp).toLocaleString()}
                  </div>
                )}
              </div>

              {/* 7-icon row under expert message */}
              {isExpert && (
                <div
                  className="msg-actions"
                  style={{
                    display: "flex",
                    gap: 10,
                    alignItems: "center",
                    marginTop: 8,
                    marginBottom: 8,
                    flexWrap: "wrap",
                    color: "#7c8894",
                  }}
                >
                  <button
                    className="msg-action-btn"
                    title="Copy"
                    aria-label="Copy"
                    onClick={() => navigator.clipboard?.writeText(msg.text || "")}
                    style={btnStyle}
                  >
                    <FiCopy />
                  </button>

                  {chatFeedbacks[msg.id] !== "bad" && (
                    <button
                      className="msg-action-btn"
                      title="Good"
                      aria-label="Good"
                      onClick={() => LocalData("good", msg.id)}
                      style={btnStyle}
                    >
                      <FiThumbsUp
                        color={chatFeedbacks[msg.id] === "good" ? "green" : "inherit"}
                      />
                    </button>
                  )}

                  {chatFeedbacks[msg.id] !== "good" && (
                    <button
                      className="msg-action-btn"
                      title="Bad"
                      aria-label="Bad"
                      onClick={() => LocalData("bad", msg.id)}
                      style={btnStyle}
                    >
                      <FiThumbsDown
                        color={chatFeedbacks[msg.id] === "bad" ? "red" : "inherit"}
                      />
                    </button>
                  )}

                  <button
                    className="msg-action-btn"
                    title="Read aloud"
                    aria-label="TTS"
                    onClick={() => readAloud(cleanText(msg.text))}
                    style={btnStyle}
                  >
                    <FiVolume2 />
                  </button>
                  <button
                    className="msg-action-btn"
                    title="Edit journal"
                    aria-label="Edit journal"
                    onClick={() => openPanel("journal")}
                    style={btnStyle}
                  >
                    <FiEdit />
                  </button>
                  <button
                    className="msg-action-btn"
                    title="Mood tracker"
                    aria-label="Mood tracker"
                    onClick={() => openPanel("mood")}
                    style={btnStyle}
                  >
                    <FiSmile />
                  </button>
                  <button
                    className="msg-action-btn"
                    title="Retry"
                    aria-label="Retry"
                    onClick={() => retrymsg("retry", msg.id)}
                    style={btnStyle}
                  >
                    <FiRefreshCw />
                  </button>
                </div>
              )}
            </React.Fragment>
          );
        })}

        <div ref={messagesEndRef} />
      </div>

      {/* Floating scroll-to-bottom arrow */}
      {showScrollArrow && (
        <button
          className="scroll-to-bottom"
          onClick={scrollToBottom}
          aria-label="Scroll to latest"
        >
          <FiChevronDown size={20} />
        </button>
      )}

      {/* Input + controls */}
      <div className="chat-bottom-container">
        <div className="input-box">
          <textarea
            ref={textareaRef}
            className={`chat-textarea-single ${isOverflowFS ? "is-scrolling" : ""}`}
            placeholder={`Ask Your ${displayExpert}`}
            value={userInput}
            onChange={(e) => {
              setUserInput(e.target.value);
              autoResizeInternal();
              if (typeof autoResize === "function") autoResize(e);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            rows={1}
          />
        </div>

        <div className="chat-controls-row">
          {/* Psychologist */}
          <button
            className={`icon-btn icon-btn--white ${displayExpert === "psychologist" ? "active" : ""}`}
            onClick={() => handleSelectExpert("psychologist")}
            aria-label="AI Psychologist"
            title="Doctor"
          >
            <img src={aipsychologistIcon} alt="Psychologist" width={24} height={24} />
          </button>

          {/* Dietician */}
          <button
            className={`icon-btn icon-btn--white ${displayExpert === "dietician" ? "active" : ""}`}
            onClick={() => handleSelectExpert("dietician")}
            aria-label="AI Dietician"
            title="Dietician"
          >
            <img src={aidieticianIcon} alt="Dietician" width={24} height={24} />
          </button>

          {/* Insights */}
          <button
            className="icon-btn icon-btn--white"
            onClick={() => openPanel("insights")}
            aria-label="Insights"
            title="Insights"
          >
            <img src={insightsIcon} alt="Insights" width={24} height={24} />
          </button>

          {/* Mic */}
          <button
            onClick={toggleRecording}
            disabled={showTyping}
            className="icon-btn icon-btn--white"
            aria-label={isRecording ? "Stop recording" : "Start recording"}
            title={isRecording ? "Stop recording" : "Start recording"}
          >
            {isRecording ? <FaStop /> : <MicIcon />}
          </button>

          {/* Audio/Video */}
          <button
            className="icon-btn icon-btn--primary"
            onClick={() => handleModalOpen("audio")}
            aria-label="Audio"
            title="Audio"
          >
            <AudioIcon width={20} height={20} />
          </button>
          <button
            className="icon-btn icon-btn--primary"
            onClick={() => handleModalOpen("video")}
            aria-label="Video"
            title="Video"
          >
            <VideoIcon width={20} height={20} />
          </button>

          {/* Send */}
          <button
            className="icon-btn icon-btn--primary send-btn"
            onClick={handleSend}
            disabled={!userInput || !userInput.trim()}
            aria-label="Send"
            title="Send"
          >
            <img src={SendIcon} alt="Send" width={20} height={20} />
          </button>
        </div>
      </div>

      {/* SlideOver */}
      <SlideOver
        title={
          panel === "insights"
            ? "Insights"
            : panel === "mood"
            ? "Mood"
            : panel === "journal"
            ? "Journal"
            : ""
        }
        isOpen={!!panel}
        onClose={closePanel}
      >
        {panel && (
          <Suspense fallback={<div>Loading…</div>}>
            {panel === "insights" && <InsightsPanel onClose={closePanel} />}
            {panel === "journal" && <JournalPanel onClose={closePanel} />}
            {panel === "mood" && <MoodPanel onClose={closePanel} />}
          </Suspense>
        )}
      </SlideOver>

      {/* Confirmation */}
      {showConfirmation && (
        <div className="chat-confirmation-overlay">
          <div className="chat-confirmation-box">
            <h4>Confirmation</h4>
            <p>Ending current session. Starting session with the {pendingExpert}.</p>
            <div className="chat-confirmation-buttons">
              <button
                className="outline-btn"
                onClick={() => {
                  setLocalExpert(pendingExpert);
                  setUserInput("");
                  setShowConfirmation(false);
                }}
              >
                Start New
              </button>
              <button className="filled-btn" onClick={() => setShowConfirmation(false)}>
                Stay
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// visible even if CSS isn’t loaded yet
const btnStyle = {
  border: 0,
  background: "transparent",
  padding: 6,
  lineHeight: 0,
  borderRadius: 8,
  cursor: "pointer",
};

export default FullScreenChat;
